/**
 * Law tab — full-opinion search over the OFFLINE caselaw database.
 *
 * The curated CASELAW list (data/caselaw.ts) gives quick-reference summaries;
 * this surfaces the VERBATIM public-domain opinions fetched from CourtListener
 * and shipped in data/db/caselaw.sqlite (+ data/caselaw/*.txt). Everything runs
 * on-device: the SQLite engine is WASM, the DB + text files are static assets
 * cached by the service worker, so search and reading work with zero network.
 *
 * The heavy sqlite-wasm module is lazy-imported on first search so it never
 * weighs on app startup.
 */
import type { CaseHit } from '@/data/caselaw-db';
import { getCaselawDb as getDb } from '@/data/caselaw-db-instance';
import { byId } from '@/ui/dom';
import { showModal } from '@/ui/overlays';
import { escapeHTML } from '@/utils/formatters';
import { linkifyToHtml, citedByHtml } from '@/features/law-cite';
import { isUnpublished, isUnconfirmedCitation, citationLabel } from '@/core/cite-status';

const VERIFY_NOTE =
  'Verbatim opinion retrieved from CourtListener (Free Law Project). ' +
  'Verify the citation and holding against the official reporter before relying on it.';

const UNPUBLISHED_NOTE =
  'UNPUBLISHED DISPOSITION — NOT binding precedent. Under Mass. App. Ct. ' +
  'Rule 23.0 (formerly Rule 1:28), a summary decision issued after ' +
  'Feb. 25, 2008 may be cited for its persuasive value only, not as binding ' +
  'precedent. Confirm the current rule before citing.';

const UNCONFIRMED_CITE_NOTE =
  'NO REPORTER CITATION — this opinion was captured as a slip opinion before a ' +
  'bound-volume citation existed, and one has not been confirmed for it. The ' +
  'docket number below identifies the case but is NOT a citation. Whether it is ' +
  'published or an unpublished Rule 23.0 disposition has not been confirmed ' +
  'either. Look it up in the official reporter before citing it.';

/** Short badge for a case flagged by the 2026-07-24 validity audit; '' for clean good-law entries. */
function validityBadge(validity: CaseHit['validity']): string {
  if (validity === 'good-law-narrowed') {
    return '<span style="display:inline-block;margin-left:6px;background:#fef9c3;color:#854d0e;border:1px solid #fde047;border-radius:6px;padding:0 5px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;vertical-align:middle;">Narrowed</span>';
  }
  if (validity === 'miscited-pinpoint') {
    return '<span style="display:inline-block;margin-left:6px;background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;border-radius:6px;padding:0 5px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;vertical-align:middle;">Check citation</span>';
  }
  return '';
}

/** One-line preview of a curated summary: collapse whitespace, truncate ~150 chars on a word boundary. */
function snippetOf(summary: string, max = 150): string {
  const s = summary.replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 60 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:]$/, '') + '…';
}

/** Run a full-text search over the offline opinions and render the results. */
export function searchOpinions(): void {
  const input = byId<HTMLInputElement>('law-op-search');
  const results = byId('law-op-results');
  if (!results) return;
  const q = input ? input.value.trim() : '';
  if (!q) { results.innerHTML = ''; return; }
  results.innerHTML =
    '<div style="font-size:12px;color:var(--text-muted);padding:8px 2px;">Searching the offline opinion library…</div>';
  void (async () => {
    const db = await getDb();
    if (!db) {
      results.innerHTML =
        '<div style="font-size:12px;color:#b45309;padding:8px 2px;">Offline opinion database unavailable on this device.</div>';
      return;
    }
    let hits: CaseHit[] = [];
    try {
      hits = db.searchCases(q, 30);
    } catch {
      hits = [];
    }
    if (!hits.length) {
      results.innerHTML =
        `<div style="font-size:12px;color:var(--text-muted);padding:8px 2px;">No full opinions matched “${escapeHTML(q)}”.</div>`;
      return;
    }
    let html =
      `<div style="font-size:10px;font-weight:700;color:var(--text-muted);text-transform:uppercase;letter-spacing:.4px;margin:4px 2px 6px;">${hits.length} full opinion${hits.length === 1 ? '' : 's'}</div>`;
    for (const h of hits) {
      const meta = [citationLabel(h.citation), h.court, h.decided ? h.decided.slice(0, 4) : '']
        .filter(Boolean)
        .map((s) => escapeHTML(String(s)))
        .join(' · ');
      const unpubBadge = isUnpublished(h.citation)
        ? '<span style="display:inline-block;margin-left:6px;background:#fef3c7;color:#92400e;border:1px solid #fde68a;border-radius:6px;padding:0 5px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;vertical-align:middle;">Unpublished</span>'
        : '';
      const unconfBadge = isUnconfirmedCitation(h.citation)
        ? '<span style="display:inline-block;margin-left:6px;background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;border-radius:6px;padding:0 5px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;vertical-align:middle;">No citation</span>'
        : '';
      const snippet = h.summary ? snippetOf(h.summary) : '';
      html +=
        `<button onclick="openOpinion('${escapeHTML(h.docId)}')" style="display:block;width:100%;text-align:left;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:9px 11px;margin-bottom:6px;cursor:pointer;">` +
        `<div style="font-size:13px;font-weight:600;color:#1e293b;">${escapeHTML(h.name)}${unpubBadge}${unconfBadge}${validityBadge(h.validity)}</div>` +
        (meta ? `<div style="font-size:11px;color:#64748b;margin-top:2px;">${meta}</div>` : '') +
        (snippet ? `<div style="font-size:11px;color:#475569;margin-top:4px;line-height:1.5;">${escapeHTML(snippet)}</div>` : '') +
        '</button>';
    }
    results.innerHTML = html;
  })();
}

/** Open one opinion's verbatim full text in a modal. */
export function openOpinion(docId: string): void {
  void (async () => {
    const db = await getDb();
    if (!db) return;
    const hit = db.getCase(docId);
    showModal('<div style="padding:20px;color:var(--text-muted);font-size:13px;">Loading full opinion…</div>');
    let text: string | null = null;
    try {
      text = await db.getCaseText(docId);
    } catch {
      text = null;
    }
    const title = hit ? escapeHTML(hit.name) : escapeHTML(docId);
    const cite = hit && hit.citation ? escapeHTML(citationLabel(hit.citation)) : '';
    const court = hit && hit.court ? escapeHTML(hit.court) : '';
    const decided = hit && hit.decided ? escapeHTML(hit.decided) : '';
    const meta = [court, decided].filter(Boolean).join(' · ');
    const source =
      hit && hit.url
        ? `<a href="${escapeHTML(hit.url)}" target="_blank" rel="noopener" style="color:var(--blue-primary);text-decoration:none;font-weight:600;">↗ Source: CourtListener (Free Law Project)</a>`
        : 'Source: CourtListener (Free Law Project)';
    const bodyHtml = text
      ? await linkifyToHtml(text)
      : '<em>Full text is not available offline for this opinion.</em>';
    const citedBy = await citedByHtml(docId);

    // Curated quick-reference: synopsis + key holdings, above the verbatim text.
    const summaryBlock =
      hit && hit.summary
        ? `<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 12px;margin-bottom:10px;">` +
          `<div style="font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;">Curated summary</div>` +
          `<div style="font-size:12px;color:#1e3a5f;line-height:1.7;">${escapeHTML(hit.summary)}</div>` +
          '</div>'
        : '';
    const holdingsBlock =
      hit && hit.holdings.length
        ? `<div style="background:#f8fafc;border-left:3px solid #1d4ed8;border-radius:0 8px 8px 0;padding:9px 12px;margin-bottom:12px;">` +
          `<div style="font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px;">Key holdings</div>` +
          hit.holdings
            .map(
              (h, i) =>
                `<div style="display:flex;gap:8px;${i > 0 ? 'margin-top:5px;padding-top:5px;border-top:1px solid #e2e8f0;' : ''}">` +
                `<span style="flex-shrink:0;width:16px;height:16px;border-radius:50%;background:#1d4ed8;color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;">${i + 1}</span>` +
                `<span style="font-size:11px;color:#1e293b;line-height:1.6;">${escapeHTML(h)}</span></div>`
            )
            .join('') +
          '</div>'
        : '';
    const unpubBanner = isUnpublished(hit?.citation)
      ? `<div style="background:#fff1f2;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:9px 11px;margin-bottom:10px;font-size:11px;color:#991b1b;line-height:1.6;font-weight:600;">🚫 ${UNPUBLISHED_NOTE}</div>`
      : '';
    const unconfBanner = isUnconfirmedCitation(hit?.citation)
      ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:9px 11px;margin-bottom:10px;font-size:11px;color:#991b1b;line-height:1.6;font-weight:600;">🚫 ${UNCONFIRMED_CITE_NOTE}</div>`
      : '';
    const validityBanner =
      hit && hit.validity !== 'good-law' && hit.validityNote
        ? `<div style="background:${hit.validity === 'miscited-pinpoint' ? '#fef2f2' : '#fefce8'};border:1px solid ${hit.validity === 'miscited-pinpoint' ? '#fecaca' : '#fde047'};border-radius:8px;padding:9px 11px;margin-bottom:10px;font-size:11px;color:${hit.validity === 'miscited-pinpoint' ? '#991b1b' : '#854d0e'};line-height:1.6;font-weight:600;">${hit.validity === 'miscited-pinpoint' ? '⚠ CHECK CITATION' : '⚠ NARROWED BY LATER AUTHORITY'} — ${escapeHTML(hit.validityNote)}</div>`
        : '';
    // A good-law case can still carry a note worth reading — how its citation
    // was confirmed, or that a same-day unpublished companion decision exists
    // and must not be conflated with it. The banner above deliberately fires
    // only on PROBLEMS, so these were written to the record and then never
    // shown. Rendered informationally rather than as a warning.
    const citeNoteBanner =
      hit && hit.validity === 'good-law' && hit.validityNote && !isUnconfirmedCitation(hit.citation)
        ? `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:9px 11px;margin-bottom:10px;font-size:11px;color:#475569;line-height:1.6;">ℹ ${escapeHTML(hit.validityNote)}</div>`
        : '';
    showModal(
      `<div style="font-family:Georgia,serif;font-weight:600;font-size:16px;color:#1e293b;margin-bottom:2px;">${title}</div>` +
        (cite ? `<div style="font-size:12px;color:#64748b;">${cite}</div>` : '') +
        (meta ? `<div style="font-size:11px;color:#94a3b8;margin-top:1px;">${meta}</div>` : '') +
        `<div style="font-size:11px;margin:8px 0 10px;">${source}</div>` +
        unpubBanner +
        unconfBanner +
        citeNoteBanner +
        validityBanner +
        `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:9px 11px;margin-bottom:12px;font-size:11px;color:#78350f;line-height:1.6;">⚠ ${VERIFY_NOTE}</div>` +
        summaryBlock +
        holdingsBlock +
        citedBy +
        `<div style="max-height:60vh;overflow:auto;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-family:Georgia,serif;font-size:13px;line-height:1.75;color:#1e293b;">${bodyHtml}</div>`
    );
  })();
}
